import React, {useState, useEffect} from 'react';
import {Table, OverlayTrigger, Tooltip} from 'react-bootstrap';
import 'jquery-confirm/css/jquery-confirm.css';
import 'jquery-confirm';
import {getVersionList} from '../../../../services/UploadRecordsApi';
import {truncateMiddleString} from '../../../../utils/stringUtils';

interface DataTableProps {
  headers: {
    key: string;
    label: string;
    sortable?: boolean;
    items?: any;
    style?: any;
    isNumeric?: boolean;
  }[];
  rows?: any[];
  onAction?: (action: string, row: any) => void;
  onSortAction?: (row: any, action?: string) => void;
  onFileAction?: (action: string, row: any) => void;
  onDownloadAction?: (action: string, row: any) => void;
  onViewPdf?: (row: any) => void;
  onRowSelect?: (row: any) => void;
  selectedRow?: any;
  disabled?: boolean;
  isLogTable?: boolean;
  children_headers?: any;
  documentType?: string;
}

const DataTable: React.FC<DataTableProps> = ({
  headers,
  rows = [],
  onAction,
  onSortAction,
  onFileAction,
  onDownloadAction,
  onViewPdf,
  disabled = false,
  isLogTable = false,
  children_headers,
  documentType,
}) => {
  const [sortedRows, setSortedRows] = useState<any[]>(rows);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [rowChildren, setRowChildren] = useState<Record<string, any[]>>({});

  useEffect(() => {
    setSortedRows(rows || []);
  }, [rows]);

  const handleToggleExpand = async (rowId: string) => {
    setExpandedRows((prev) => {
      const updated = new Set(prev);
      if (updated.has(rowId)) {
        updated.delete(rowId);
      } else {
        updated.add(rowId);
      }
      return updated;
    });

    // Fetch only if not already loaded
    if (!rowChildren[rowId]) {
      try {
        const response = await getVersionList(rowId);
        const data = response?.result;
        setRowChildren((prev) => ({...prev, [rowId]: data}));
      } catch (error) {
        console.error('Failed to fetch child rows:', error);
      }
    }
  };

  return (
    <div className='table-responsive border-0 mb-2'>
      <Table style={{tableLayout: 'fixed'}}>
        <thead>
          <tr>
            {headers.map((header) => (
              <th
                key={header.key}
                style={{
                  border: 'none',
                  padding: '8px',
                  backgroundColor: '#e0f7fa',
                  color: '#000',
                  cursor: header.sortable ? 'pointer' : 'default',
                  position: 'sticky',
                  top: 0,
                  zIndex: -90,
                  textAlign: [
                    'isActiveDetails',
                    'appointmentStatusDesc',
                    'actions',
                    'phonenumber',
                    'download',
                  ].includes(header.key)
                    ? 'center'
                    : 'left',
                  ...header.style,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: [
                      'isActiveDetails',
                      'appointmentStatusDesc',
                      'actions',
                      'phonenumber',
                      'contactphonenumber',
                      'download',
                    ].includes(header.key)
                      ? 'space-evenly'
                      : 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>{header.label}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {sortedRows?.length > 0 ? (
            sortedRows?.map((row, index) => {
              const rowId = row.parentCaseRecordDetailsId || row.id;
              const isExpanded = expandedRows.has(rowId);

              return (
                <React.Fragment key={row.id}>
                  <tr
                    style={{
                      backgroundColor: index % 2 === 1 ? 'white' : '#f2f2f2',
                    }}
                  >
                    {headers.map((header) => {
                      const cellContent = row[header.key];

                      if (header.key === 'fileName') {
                        return (
                          <td key={header.key}>
                            <span
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              {row.revisionNo > 1 && (
                                <span
                                  className="material-symbols-outlined"
                                  style={{
                                    cursor: "pointer",
                                    color: "#0098d7",
                                    fontSize: "20px",
                                    margin: "0px 5px",
                                  }}
                                  onClick={() => handleToggleExpand(rowId)}
                                >
                                  {isExpanded
                                    ? "do_not_disturb_on"
                                    : "add_circle"}
                                </span>
                              )}

                              <OverlayTrigger
                                placement="auto"
                                delay={{ show: 200, hide: 10 }}
                                overlay={
                                  cellContent ? (
                                    <Tooltip
                                      id={`tooltip-${header.key}-${row.id}`}
                                    >
                                      {cellContent}
                                    </Tooltip>
                                  ) : (
                                    <></>
                                  )
                                }
                              >
                                <span
                                  className={`d-inline-block ${
                                    !isLogTable ? "text-truncate" : ""
                                  }`}
                                  style={{
                                    maxWidth: isLogTable ? "none" : "100%",
                                    cursor: isLogTable ? "default" : "pointer",
                                  }}
                                >
                                  {cellContent && cellContent?.length > 20
                                    ? truncateMiddleString(cellContent)
                                    : cellContent}
                                </span>
                              </OverlayTrigger>
                              {row.noOfPages > 0 && (
                                <>
                                  {" "}
                                  <span
                                    style={{
                                      color: "#0098d7",
                                      marginLeft: "5px",
                                    }}
                                  >
                                    {row.noOfPages} pages{" "}
                                    {(documentType === "Transcription" ||
                                      documentType === "Draft Transcription") &&
                                      ` | ${row?.totalLineCount || 0} lines`}
                                  </span>
                                </>
                              )}
                            </span>
                          </td>
                        );
                      }

                      if (header.key === 'download') {
                        const fileUrl = row.download;
                        const isPdf = fileUrl?.toLowerCase().endsWith('.pdf');
                        const isImage =
                          /\.(png|jpe?g|gif|tiff?|heif|heic|webp)$/i.test(
                            fileUrl || '',
                          );

                        return (
                          <td key={header.key} style={{padding: '8px'}}>
                            {fileUrl && (
                              <>
                                {(isPdf || isImage) && (
                                  <span
                                    className='material-icons material-symbols-outlined color_brand mx-1'
                                    onClick={() => onViewPdf?.(row)} // Unified viewer handler
                                    style={{cursor: 'pointer'}}
                                    title='View File'
                                  >
                                    document_search
                                  </span>
                                )}
                                <span
                                  className='material-icons color_brand mx-1'
                                  onClick={() =>
                                    onDownloadAction?.(row, 'download')
                                  }
                                  style={{cursor: 'pointer'}}
                                >
                                  download
                                </span>
                              </>
                            )}
                          </td>
                        );
                      }

                      return (
                        <td
                          key={header.key}
                          style={{
                            padding: header.isNumeric
                              ? '8px 20px 8px 8px'
                              : '8px',
                            textAlign: header.isNumeric ? 'center' : 'left',
                          }}
                        >
                          <OverlayTrigger
                            placement='auto'
                            delay={{show: 200, hide: 10}}
                            overlay={
                              cellContent &&
                              cellContent.length > (isLogTable ? 60 : 15) ? (
                                <Tooltip id={`tooltip-${header.key}-${row.id}`}>
                                  {cellContent}
                                </Tooltip>
                              ) : (
                                <></>
                              )
                            }
                          >
                            <span
                              className={`d-inline-block ${
                                !isLogTable ? 'text-truncate' : ''
                              }`}
                              style={{
                                maxWidth: isLogTable ? 'none' : '100%',
                                cursor: isLogTable ? 'default' : 'pointer',
                              }}
                            >
                              {cellContent}
                            </span>
                          </OverlayTrigger>
                        </td>
                      );
                    })}
                  </tr>

                  {isExpanded &&
                    rowChildren[rowId]?.map((child: any) => (
                      <tr
                        key={child.caseRecordDetailsId}
                        style={{backgroundColor: '#eef8fb'}}
                      >
                        {children_headers.map((header: any) => {
                          if (header.key === 'revisionNoDesc') {
                            return (
                              <td key={header.key}>
                                {child.revisionNoDesc && (
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "start",
                                      gap: "4px",
                                      marginLeft: "30px",
                                    }}
                                  >
                                    <span
                                      className="material-symbols-outlined"
                                      style={{
                                        fontSize: "18px",
                                        color:
                                          child.isLatestVersion == 1
                                            ? "#50dd32"
                                            : "#0098d7",
                                      }}
                                    >
                                      {child.isLatestVersion == 1
                                        ? "charger"
                                        : "history"}
                                    </span>
                                    {child[header.key]}
                                    <OverlayTrigger
                                      placement="auto"
                                      delay={{ show: 200, hide: 10 }}
                                      overlay={
                                        child.fileName ? (
                                          <Tooltip
                                            id={`tooltip-${header.key}-${child.id}`}
                                          >
                                            {child.fileName}
                                          </Tooltip>
                                        ) : (
                                          <></>
                                        )
                                      }
                                    >
                                      <span
                                        className={`d-inline-block ${
                                          !isLogTable ? "text-truncate" : ""
                                        }`}
                                        style={{
                                          maxWidth: isLogTable
                                            ? "none"
                                            : "100%",
                                          cursor: isLogTable
                                            ? "default"
                                            : "pointer",
                                        }}
                                      >
                                        {child.fileName?.length > 10
                                          ? truncateMiddleString(child.fileName)
                                          : child.fileName}
                                      </span>
                                    </OverlayTrigger>

                                    {child.noOfPages > 0 && (
                                      <>
                                        {" "}
                                        <span style={{ color: "#0098d7" }}>
                                          {child.noOfPages} pages{" "}
                                          {(documentType === "Transcription" ||
                                            documentType ===
                                              "Draft Transcription") &&
                                            ` | ${row?.noOfLines || 0} lines`}
                                        </span>
                                      </>
                                    )}
                                  </span>
                                )}
                              </td>
                            );
                          }

                          if (header.key === 'download') {
                            const fileUrl = row.download;
                            const isPdf = fileUrl
                              ?.toLowerCase()
                              .endsWith('.pdf');
                            const isImage =
                              /\.(png|jpe?g|gif|tiff?|heif|heic|webp)$/i.test(
                                fileUrl || '',
                              );

                            return (
                              <td key={header.key} style={{padding: '8px'}}>
                                {fileUrl && (
                                  <>
                                    {(isPdf || isImage) && (
                                      <span
                                        className='material-icons material-symbols-outlined color_brand mx-1'
                                        onClick={() => onViewPdf?.(child)} // Unified viewer handler
                                        style={{cursor: 'pointer'}}
                                        title='View File'
                                      >
                                        document_search
                                      </span>
                                    )}
                                    <span
                                      className='material-icons color_brand mx-1'
                                      onClick={() =>
                                        onDownloadAction?.(child, 'download')
                                      }
                                      style={{cursor: 'pointer'}}
                                    >
                                      download
                                    </span>
                                  </>
                                )}
                              </td>
                            );
                          }

                          return (
                            <td key={header.key}>
                              <OverlayTrigger
                                placement='auto'
                                delay={{show: 200, hide: 10}}
                                overlay={
                                  child[header.key] &&
                                  child[header.key].length > 15 ? (
                                    <Tooltip
                                      id={`tooltip-${header.key}-${child.id}`}
                                    >
                                      {child[header.key]}
                                    </Tooltip>
                                  ) : (
                                    <></>
                                  )
                                }
                              >
                                <span
                                  className='d-inline-block text-truncate'
                                  style={{
                                    maxWidth: '100%',
                                    cursor: 'default',
                                  }}
                                >
                                  {child[header.key]}
                                </span>
                              </OverlayTrigger>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                </React.Fragment>
              );
            })
          ) : (
            <tr>
              <td colSpan={headers.length} style={{textAlign: 'center'}}>
                <span className='no_rec_data'>No Record Found</span>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default DataTable;
